import { useState } from 'react';
import Button from '../../components/common/Button';
import Icon from '../../components/common/Icon';
import { useToast } from '../../context/ToastContext';
import './GlobalSettings.css';

const GlobalSettings = () => {
  const { showToast } = useToast();
  const [settings, setSettings] = useState({
    platformName: 'Auth1',
    supportEmail: 'support@auth1.com',
    defaultLanguage: 'en',
    sessionTimeout: 30,
    mfaRequired: false,
    allowSignups: true,
    requireEmailVerification: true,
    passwordMinLength: 8,
    maxLoginAttempts: 5,
    lockoutDuration: 15
  });

  const handleSave = () => {
    showToast('Settings saved successfully', 'success');
  };

  const handleChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className="settings-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Global Settings</h1>
          <p className="page-subtitle">Configure platform-wide settings and defaults</p>
        </div>
        <div className="page-actions">
          <Button variant="secondary">Cancel</Button>
          <Button variant="primary" icon="check" onClick={handleSave}>Save Changes</Button>
        </div>
      </div>

      {/* General Settings */}
      <section className="section">
        <div className="section-header">
          <h3 className="section-title">
            <Icon name="settings" size="md" />
            General
          </h3>
        </div>

        <div className="settings-grid">
          <div className="form-group">
            <label className="form-label">Platform Name</label>
            <input
              type="text"
              className="form-input"
              value={settings.platformName}
              onChange={(e) => handleChange('platformName', e.target.value)}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Support Email</label>
            <input
              type="email"
              className="form-input"
              value={settings.supportEmail}
              onChange={(e) => handleChange('supportEmail', e.target.value)}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Default Language</label>
            <select
              className="form-select"
              value={settings.defaultLanguage}
              onChange={(e) => handleChange('defaultLanguage', e.target.value)}
            >
              <option value="en">English</option>
              <option value="es">Spanish</option>
              <option value="fr">French</option>
              <option value="de">German</option>
              <option value="zh">Chinese</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Session Timeout (minutes)</label>
            <input
              type="number"
              className="form-input"
              value={settings.sessionTimeout}
              onChange={(e) => handleChange('sessionTimeout', parseInt(e.target.value))}
            />
          </div>
        </div>
      </section>

      {/* Security Settings */}
      <section className="section">
        <div className="section-header">
          <h3 className="section-title">
            <Icon name="shield" size="md" />
            Security
          </h3>
        </div>

        <div className="settings-list">
          <div className="setting-item">
            <div className="setting-info">
              <div className="setting-label">Require MFA for all users</div>
              <div className="setting-description">Force multi-factor authentication for enhanced security</div>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={settings.mfaRequired}
                onChange={(e) => handleChange('mfaRequired', e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          <div className="setting-item">
            <div className="setting-info">
              <div className="setting-label">Require email verification</div>
              <div className="setting-description">Users must verify their email before accessing the platform</div>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={settings.requireEmailVerification}
                onChange={(e) => handleChange('requireEmailVerification', e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          <div className="setting-item">
            <div className="setting-info">
              <div className="setting-label">Allow public signups</div>
              <div className="setting-description">Allow new users to create accounts without invitation</div>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={settings.allowSignups}
                onChange={(e) => handleChange('allowSignups', e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
        </div>

        <div className="settings-grid mt-6">
          <div className="form-group">
            <label className="form-label">Minimum Password Length</label>
            <input
              type="number"
              className="form-input"
              value={settings.passwordMinLength}
              onChange={(e) => handleChange('passwordMinLength', parseInt(e.target.value))}
              min="6"
              max="32"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Max Login Attempts</label>
            <input
              type="number"
              className="form-input"
              value={settings.maxLoginAttempts}
              onChange={(e) => handleChange('maxLoginAttempts', parseInt(e.target.value))}
              min="3"
              max="10"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Lockout Duration (minutes)</label>
            <input
              type="number"
              className="form-input"
              value={settings.lockoutDuration}
              onChange={(e) => handleChange('lockoutDuration', parseInt(e.target.value))}
              min="5"
              max="60"
            />
          </div>
        </div>
      </section>

      {/* Danger Zone */}
      <section className="section danger-section">
        <div className="section-header">
          <h3 className="section-title danger">
            <Icon name="alert-triangle" size="md" />
            Danger Zone
          </h3>
        </div>

        <div className="danger-actions">
          <div className="danger-item">
            <div className="danger-info">
              <div className="danger-label">Reset all tenant settings</div>
              <div className="danger-description">This will reset all tenant configurations to defaults</div>
            </div>
            <Button variant="danger" size="sm">Reset All</Button>
          </div>

          <div className="danger-item">
            <div className="danger-info">
              <div className="danger-label">Purge all sessions</div>
              <div className="danger-description">Force logout all users across all tenants</div>
            </div>
            <Button variant="danger" size="sm">Purge Sessions</Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GlobalSettings;
